import React, { useEffect, useState, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from "chart.js";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

// Purple scheme
const COLORS = {
  Service: "#9848c7", // primary for purchases
  Inne: "#cdb0ea",    // lighter purple
};

const regionNameMapping = {
  MAZOWIECKIE: "mazowieckie",
  ŚLĄSKIE: "śląskie",
  PODKARPACKIE: "podkarpackie",
  LUBUSKIE: "lubuskie",
  ZACHODNIOPOMORSKIE: "zachodniopomorskie",
  WIELKOPOLSKIE: "wielkopolskie",
  DOLNOŚLĄSKIE: "dolnośląskie",
  ŁÓDZKIE: "łódzkie",
  ŚWIĘTOKRZYSKIE: "świętokrzyskie",
  POMORSKIE: "pomorskie",
  LUBELSKIE: "lubelskie",
  OPOLSKIE: "opolskie",
  PODLASKIE: "podlaskie",
  "WARMIŃSKO-MAZURSKIE": "warmińsko-mazurskie",
  "KUJAWSKO-POMORSKIE": "kujawsko-pomorskie",
  MAŁOPOLSKIE: "małopolskie",
};

const parseValue = (value) => {
  const num = parseFloat(value);
  return isNaN(num) ? 0 : num;
};

const formatNumber = (value) => {
  if (typeof value !== "number") return value;
  return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

const monthsList = [
  "Styczeń","Luty","Marzec","Kwiecień","Maj","Czerwiec",
  "Lipiec","Sierpień","Wrzesień","Październik","Listopad","Grudzień"
];

// === Styled Components ===
const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const ZakupyTypesByRegion = ({ zakupyData = [], clientsData = [] }) => {
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("00");

  useEffect(() => {
    if (zakupyData.length === 0) return;
    const years = [...new Set(zakupyData.map((row) => row.Rok?.toString()))].sort();
    setAvailableYears(years);
    setSelectedYear(years.includes("2024") ? "2024" : years[0]);
  }, [zakupyData]);

  const chartData = useMemo(() => {
    const regionTotals = {};
    Object.values(regionNameMapping).forEach((region) => {
      regionTotals[region] = { Service: 0, Inne: 0 };
    });

    zakupyData
      .filter(
        (row) =>
          row.Rok?.toString() === selectedYear &&
          (selectedMonth === "00" || parseInt(row.Miesiac) === parseInt(selectedMonth))
      )
      .forEach((row) => {
        const client = clientsData.find(
          (c) => c.nip?.trim() === row.NrDostawcy?.trim()
        );
        const normalizedRegion = client?.wojewodztwo?.trim().toUpperCase();
        const region = regionNameMapping[normalizedRegion];
        if (!region) return;

        const net = parseValue(row.K_42);
        const vat = parseValue(row.K_43);
        const total = net + vat;

        // Heuristic: 15% treated as services, rest as "Inne"
        const serviceAmount = total * 0.15;
        const inneAmount = Math.max(0, total - serviceAmount);

        regionTotals[region].Service += serviceAmount;
        regionTotals[region].Inne += inneAmount;
      });

    const labels = Object.keys(regionTotals);

    return {
      labels,
      datasets: [
        {
          label: "Usługi",
          data: labels.map((r) => regionTotals[r].Service),
          backgroundColor: COLORS.Service,
          stack: "types",
        },
        {
          label: "Inne",
          data: labels.map((r) => regionTotals[r].Inne),
          backgroundColor: COLORS.Inne,
          stack: "types",
        },
      ],
    };
  }, [zakupyData, clientsData, selectedYear, selectedMonth]);

  return (
    <div style={{ width: "100%", margin: "20px auto", textAlign: "center" }}>
      <ChartTitle>
        Podział zakupów: Usługi vs Inne (województwa)
        <InfoIconWrapper data-tooltip-id="types-region-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>

      <InfoTooltip
        id="types-region-tooltip"
        place="right"
        style={{ fontSize: "12px", fontFamily: "Arial, sans-serif", color: "#fff" }}
        content={
          <>
            <strong>Usługi vs Inne</strong><br />
            Usługi = 15% wartości zakupu (heurystyka).<br />
            Inne = pozostałe 85%.<br />
            Podział dla każdego województwa.
          </>
        }
      />

      <div style={{
        marginBottom: "20px",
        display: "flex",
        justifyContent: "center",
        gap: "12px",
        alignItems: "center"
      }}>
        <label htmlFor="year" style={{ fontWeight: 500 }}>Rok:</label>
        <select
          id="year"
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.target.value)}
          style={{
            padding: "8px 12px",
            fontSize: "15px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            cursor: "pointer"
          }}
        >
          {availableYears.map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>

        <label htmlFor="month" style={{ fontWeight: 500 }}>Miesiąc:</label>
        <select
          id="month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          style={{
            padding: "8px 12px",
            fontSize: "15px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            cursor: "pointer"
          }}
        >
          <option value="00">Cały rok</option>
          {monthsList.map((m, idx) => (
            <option key={idx} value={idx + 1}>{m}</option>
          ))}
        </select>
      </div>

      <div style={{ width: "100%", maxWidth: "1100px", height: "600px", margin: "0 auto" }}>
        <Bar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { position: "bottom" },
              tooltip: {
                backgroundColor: "#333",
                titleColor: "#fff",
                bodyColor: "#fff",
                callbacks: {
                  title: () => "",
                  label: (context) =>
                    `${context.dataset.label}: ${formatNumber(context.raw)}`
                }
              },
            },
            scales: {
              x: { stacked: true },
              y: {
                stacked: true,
                title: {
                  display: true,
                  text: "Kwota",
                  color: "#666",
                  font: { size: 12, weight: "bold" }
                },
                ticks: {
                  callback: (value) => formatNumber(value)
                }
              },
            },
          }}
        />
      </div>
    </div>
  );
};

export default ZakupyTypesByRegion;
